// frontend/src/components/McpConnections/api.js

// Assuming API_URL is imported from your services config as requested.
// If not, you can define it here.
import { API_URL, fetchWithHandling } from '../../services/api';
const API_BASE_URL = API_URL; // Using a fallback for demonstration

// === EXPORTED API FUNCTIONS ===
// Each function now accepts an 'accessToken' and uses the fetchWithHandling wrapper.

/**
 * Fetches all connections for the authenticated user.
 * @param {string} accessToken The user's JWT.
 */
export const fetchConnections = (accessToken) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};
export const fetchOqlousTools = (accessToken) => {
  return fetchWithHandling(`${API_BASE_URL}/oqlous/tools`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

/**
 * Checks if a given SSE URL has OAuth capabilities.
 * @param {string} accessToken The user's JWT.
 * @param {string} sseUrl The URL to check.
 */
export const checkUrl = (accessToken, sseUrl, transport_type) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/check`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      sse_url: sseUrl,
      transport_type: transport_type || 'sse',
    }),
  });
};

/**
 * Initiates a new OAuth 2.0 connection flow.
 * @param {string} accessToken The user's JWT.
 * @param {object} data The connection details ({ origin, connection_name, sse_url }).
 */
export const initiateOauth = (accessToken, data) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/oauth/initiate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * Saves a new connection that uses custom headers for authentication.
 * @param {string} accessToken The user's JWT.
 * @param {object} data The connection details ({ connection_name, sse_url, headers }).
 */
export const saveCustomConnection = (accessToken, data) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/custom`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * ✅ NEW: Saves a new connection that uses STDIO for transport.
 * @param {string} accessToken The user's JWT.
 * @param {object} data The connection details ({ connection_name, command, args }).
 */
export const saveStdioConnection = (accessToken, data) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/stdio`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * Deletes a specific connection.
 * @param {string} accessToken The user's JWT.
 * @param {string} connectionId The ID of the connection to delete.
 */
export const deleteConnection = (accessToken, connectionId) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/${connectionId}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

/**
 * Triggers a manual re-check of a connection's status.
 * @param {string} accessToken The user's JWT.
 * @param {string} connectionId The ID of the connection to re-check.
 */
export const recheckConnection = (accessToken, connectionId) => {
  return fetchWithHandling(
    `${API_BASE_URL}/mcp/connections/${connectionId}/recheck`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

/**
 * Calls a specific tool on a connection.
 * @param {string} accessToken The user's JWT.
 * @param {string} connectionId The ID of the connection.
 * @param {string} toolName The name of the tool to call.
 * @param {object} args The arguments for the tool.
 */
export const callTool = (accessToken, connectionId, toolName, args) => {
  return fetchWithHandling(
    `${API_BASE_URL}/mcp/connections/${connectionId}/call-tool`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        tool_name: toolName,
        arguments: args,
      }),
    },
  );
};

/**
 * Starts the re-authentication flow for an existing OAuth connection.
 * @param {string} accessToken The user's JWT.
 * @param {string} connectionId The ID of the connection to re-authenticate.
 */
export const reauthenticateOauth = (accessToken, connectionId) => {
  return fetchWithHandling(
    `${API_BASE_URL}/mcp/connections/${connectionId}/re-authenticate`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
};

/**
 * Updates the headers for a custom authentication connection.
 * @param {string} accessToken The user's JWT.
 * @param {string} connectionId The ID of the connection.
 * @param {object} headers The new headers object.
 */
export const updateCustomHeaders = (accessToken, connectionId, headers) => {
  return fetchWithHandling(
    `${API_BASE_URL}/mcp/connections/${connectionId}/custom-headers`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ headers }),
    },
  );
};

/**
 * Triggers a background check of all user's connections.
 * @param {string} accessToken The user's JWT.
 */
export const triggerAllConnectionsCheck = (accessToken) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/check-all`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};

/**
 * Fetches all available global connection templates.
 * @param {string} accessToken The user's JWT.
 */
export const fetchGlobalConnections = (accessToken) => {
  return fetchWithHandling(`${API_BASE_URL}/mcp/connections/global`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
};
